import { ArrowRightThin } from '@ori-ui/icons';
import { styled } from '@ori-ui/mui';
import useTestId from '@ori/testid-generator';
import type { MouseEvent, FC } from 'react';
import React, { useCallback } from 'react';

import type { NavigationItem } from '../models';

const StyledSubpage = styled('li')(({ theme }) => ({
  alignItems: 'center',
  color: theme.palette.text.disabled,
  cursor: 'pointer',
  display: 'flex',
  flex: '0 0 auto',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(4),
  order: 2,
  padding: theme.spacing(0, 4),
  [theme.breakpoints.up(768)]: {
    height: '30px',
    margin: theme.spacing(0, 3),
    order: 'unset',
    padding: 0,
  },
}));

const StyledSubpageText = styled('a')(({ theme }) => ({
  color: theme.palette.text.disabled,
  overflow: 'hidden',
  textDecoration: 'none',
  textOverflow: 'ellipsis',
  width: '100%',
  '&:focus': {
    color: theme.palette.text.secondary,
  },
  '&:hover': {
    color: theme.palette.text.secondary,
  },
}));

const url2testid = (url: string): string =>
  url
    .replace(/^https?:/i, '')
    .replace(/[#?].*$/i, '')
    .replaceAll(/[./]/gi, '_');

interface SubpageLinkProps {
  subpage: NavigationItem;
  onClick: (event: MouseEvent, subpage: NavigationItem) => void;
}

export const SubpageLink: FC<SubpageLinkProps> = ({ subpage, onClick }) => {
  const { getTestId } = useTestId();
  const onSubpageLinkClick = useCallback((e: MouseEvent) => onClick(e, subpage), [onClick, subpage]);

  return (
    <StyledSubpage
      key={subpage.Label}
      data-testid={getTestId(`navlink-${url2testid(subpage.Url)}`)}
    >
      <StyledSubpageText
        href={subpage.Url}
        onClick={onSubpageLinkClick}
      >
        {subpage.Label}
        {Boolean(subpage.Children.length) && <ArrowRightThin />}
      </StyledSubpageText>
    </StyledSubpage>
  );
};
